import type { ReadonlyPropertyFilters } from '~/core/entities/property/filterDefinitions';
import type { PropertyFilters } from '~/core/entities/property/filters';

import {
	PropertyTypeEnum,
	propertyTypesSlugByValue,
	propertyTypesValueBySlug
} from '~/core/entities/CRMEnums';
import { deduplicateRegions } from '~/core/entities/property/deduplicateRegions';
import {
	isPropertyFilterKey,
	normalizePropertyFilters,
	propertyFilterDefinitions,
	readPropertyFilterQuery,
	writePropertyFilterQuery
} from '~/core/entities/property/filterDefinitions';
import { DEFAULT_LOCATION_SLUG } from '~/core/entities/property/filterPolicy';
import { slugify } from '~~/utils/slugify';

export type SearchPage = 'imoveis' | 'mapa-de-imoveis' | 'ShortsHomeFeed';
export type PropertyFiltersUrlTarget =
	| { kind: 'listing'; page: SearchPage }
	| { kind: 'curatedCollection'; slug?: string }
	| { kind: 'collectionRoot' }
	| { kind: 'ownCollection' }
	| { kind: 'sharedCollection'; slug: string };
export type ContextQuery = Readonly<
	Record<string, string | number | boolean | readonly string[] | undefined>
>;

export const DEFAULT_PROPERTY_FILTERS_URL = `/venda/imoveis/${DEFAULT_LOCATION_SLUG}`;

export function getPropertyTypeFromSlug(
	slug: string | null | undefined
): PropertyTypeEnum | null {
	if (!slug) return null;
	return (
		Object.entries(propertyTypesValueBySlug).find(
			([key]) => key === slug
		)?.[1] ?? null
	);
}

export function getTransactionTypeFromSlug(
	slug: string | null | undefined
): PropertyFilters['transactionType'] {
	return !slug ? 'rent' : slug === 'aluguel' ? 'rent' : 'sell';
}
export function getSlugFromTransactionType(
	type: PropertyFilters['transactionType'] | null | undefined
): 'venda' | 'aluguel' {
	return type === 'rent' ? 'aluguel' : 'venda';
}

function decodeSegment(value: string): string {
	let current = value;
	for (let i = 0; i < 5 && /%[0-9A-Fa-f]{2}/.test(current); i++) {
		try {
			const next = decodeURIComponent(current);
			if (next === current) break;
			current = next;
		} catch {
			break;
		}
	}
	return current;
}

export function parsePropertyFilterSlugs(raw = ''): {
	locationSlug?: string;
	addressSlug?: string;
	propertyTypeSlug?: string;
} {
	const [locationSlug, ...parts] = raw.split('/').filter(Boolean);
	if (!locationSlug) return {};
	const last = parts.at(-1);
	const type = getPropertyTypeFromSlug(last);
	const address = (type ? parts.slice(0, -1) : parts).join('/');
	return {
		locationSlug,
		...(type ? { propertyTypeSlug: last } : {}),
		...(address ? { addressSlug: decodeSegment(address) } : {})
	};
}

export function parsePropertyFiltersUrl({
	url,
	target
}: {
	url: string;
	target: PropertyFiltersUrlTarget;
}): PropertyFilters {
	const [path = '', queryString = ''] = (url.split('#')[0] ?? '').split('?');
	const query = new URLSearchParams(queryString);
	let raw = '';
	let transactionType: PropertyFilters['transactionType'] = 'sell';
	if (target.kind === 'listing') {
		const match = path.match(
			new RegExp(`^/(venda|aluguel)/${target.page}(?:/|$)`)
		);
		if (!match) return { transactionType: 'sell' };
		transactionType = getTransactionTypeFromSlug(match[1]);
		raw = path.slice(match[0].length);
	} else if (target.kind === 'curatedCollection') {
		if (!/^\/colecao(?:\/|$)/.test(path)) return { transactionType: 'sell' };
		raw = path.replace(/^\/colecao\/?/, '');
	} else if (!/^\/colecoes(?:\/|$)/.test(path))
		return { transactionType: 'sell' };
	const result = readPropertyFilterQuery(query);
	const slugs = parsePropertyFilterSlugs(raw);
	const firstType = getPropertyTypeFromSlug(slugs.propertyTypeSlug);
	result.propertyTypes = [
		...(firstType ? [firstType] : []),
		...(result.propertyTypes || [])
	];
	result.transactionType = transactionType;
	if (target.kind === 'listing' || target.kind === 'curatedCollection') {
		result.locationSlug =
			slugs.locationSlug || result.locationSlug || DEFAULT_LOCATION_SLUG;
		if (slugs.addressSlug) result.addressSlug = slugs.addressSlug;
	} else {
		const id = path
			.replace(/^\/colecoes\/?/, '')
			.replace(/^compartilhada\//, '')
			.trim();
		if (id && id !== 'minha-colecao') result.wishlistId = id;
		delete result.locationSlug;
		delete result.states;
		delete result.cities;
		delete result.regions;
		delete result.macroregions;
	}
	if (result.regions) result.regions = deduplicateRegions(result.regions);
	return result;
}

export function buildPropertyFiltersUrl({
	filters,
	target = { kind: 'listing', page: 'imoveis' },
	contextQuery = {}
}: {
	filters: ReadonlyPropertyFilters;
	target?: PropertyFiltersUrlTarget;
	contextQuery?: ContextQuery;
}): string {
	const {
		transactionType,
		propertyTypes,
		states,
		cities,
		regions,
		locationSlug: explicitLocation,
		addressSlug: explicitAddress,
		poiLocationLat,
		poiLocationLng,
		...other
	} = normalizePropertyFilters(filters);
	const state = states?.[0] || '';
	const city = state ? cities?.[0] || '' : '';
	const firstRegion = city ? regions?.[0] || '' : '';
	const locationParts = [firstRegion, city, state].filter(Boolean).map(slugify);
	const location = locationParts.length
		? [...locationParts, 'brasil'].join('-')
		: explicitLocation || DEFAULT_LOCATION_SLUG;
	const address =
		explicitAddress ||
		(explicitLocation?.includes('/')
			? explicitLocation.split('/')[0]
			: undefined);

	const [firstType, ...remainingTypes] = propertyTypes || [];
	const addressInQuery =
		address &&
		((getPropertyTypeFromSlug(address) && !firstType) ||
			/%[0-9a-f]{2}/i.test(address));
	const queryFilters = {
		...other,
		regions,
		propertyTypes: remainingTypes,
		cities,
		states,

		...(addressInQuery ? { addressSlug: address } : {}),
		...(poiLocationLat !== undefined ? { poiLocationLat } : {}),
		...(poiLocationLng !== undefined ? { poiLocationLng } : {}),
		...contextQuery
	};
	const query = writePropertyFilterQuery(queryFilters, true);
	for (const [key, value] of Object.entries(contextQuery)) {
		if (isPropertyFilterKey(key)) {
			const field = propertyFilterDefinitions[key];
			if (field.placement === 'context') {
				const serialized = field.write(value);
				if (serialized !== undefined) query.set(key, serialized);
			}
			continue;
		}
		if (Array.isArray(value)) {
			if (value.length) query.set(key, value.join(','));
		} else if (
			value ||
			value === 0 ||
			(key === 'isExclusive' && value === false)
		)
			query.set(key, String(value));
	}
	const suffix = query.size ? `?${query}` : '';
	if (target.kind === 'curatedCollection')
		return `/colecao${target.slug ? `/${target.slug}` : ''}${suffix}`;
	if (target.kind === 'collectionRoot') return `/colecoes${suffix}`;
	if (target.kind === 'ownCollection')
		return `/colecoes/minha-colecao${suffix}`;
	if (target.kind === 'sharedCollection')
		return `/colecoes/compartilhada/${target.slug}${suffix}`;
	const addressPath =
		address && !addressInQuery ? `/${encodeURIComponent(address)}` : '';
	const typePath = firstType ? `/${propertyTypesSlugByValue[firstType]}` : '';
	return `/${getSlugFromTransactionType(transactionType)}/${target.page}/${location}${addressPath}${typePath}${suffix}`;
}
